import { z } from 'zod';
import { isoDate, uuid } from './common';
import { seriesWriteupRule } from './templates';

/** How a recurring meeting is recorded. Defaults apply until somebody changes one. */
export const seriesSettings = z.object({
  seriesKey: z.string(),
  /**
   * What it is called: the name somebody gave it, else its newest meeting's
   * title, else its calendar event's — a series is a calendar series before
   * anything of it is recorded (PRD 65).
   */
  title: z.string(),
  botEnabled: z.boolean(),
  language: z.string(),
  /** What this series' notes and ledger are written in; 'auto' follows your own setting. */
  noteLanguage: z.string(),
  ledgerLanguage: z.string(),
  /** Where this series' ledger is posted (PRD 33). Null: nowhere yet. */
  slackChannelId: z.string().nullable(),
  /** Where this series' commitments go in Asana — a default the picker arrives on (PRD 51). */
  asanaWorkspaceGid: z.string().nullable(),
  asanaProjectGid: z.string().nullable(),
  slackChannelName: z.string().nullable(),
  spaceId: uuid.nullable(),
  folderId: uuid.nullable(),
  botDisplayName: z.string().nullable(),
  saveVideo: z.boolean(),
  /** After every meeting, written up with these templates — and posted where each says (PRD 61). */
  writeups: z.array(seriesWriteupRule),
  /**
   * The series' own note (PRD 65): every meeting is written up with this
   * template, its page opens on that version, and it is what Slack is sent.
   * Null: the full note.
   */
  noteTemplateId: z.string().nullable(),
  /** Send the note to `slackChannelId` after every meeting, kept up to date there. */
  postNote: z.boolean(),
  /** Why the last note could not be sent, in words — e.g. the app is not in the channel. */
  notePostError: z.string().nullable(),
});
export type SeriesSettings = z.infer<typeof seriesSettings>;

export const seriesSettingsBody = z.object({
  botEnabled: z.boolean().optional(),
  language: z.string().min(2).max(16).optional(),
  noteLanguage: z.string().min(2).max(16).optional(),
  ledgerLanguage: z.string().min(2).max(16).optional(),
  /** Both together, always: an id with no name is a channel nothing can name. */
  slackChannelId: z.string().min(1).max(64).nullable().optional(),
  asanaWorkspaceGid: z.string().min(1).max(64).nullable().optional(),
  asanaProjectGid: z.string().min(1).max(64).nullable().optional(),
  slackChannelName: z.string().min(1).max(120).nullable().optional(),
  spaceId: z.string().uuid().nullable().optional(),
  folderId: z.string().uuid().nullable().optional(),
  botDisplayName: z.string().trim().min(1).max(80).nullable().optional(),
  saveVideo: z.boolean().optional(),
  /** The whole list, replaced: at most five a series. */
  writeups: z.array(seriesWriteupRule).max(5).optional(),
  /** `stock:<key>` or a template shared with the workspace; null is the full note. */
  noteTemplateId: z.string().min(1).max(64).nullable().optional(),
  /** Only with a channel: turning it on without one is refused. */
  postNote: z.boolean().optional(),
});
export type SeriesSettingsBody = z.infer<typeof seriesSettingsBody>;

/** A recurring meeting as the People page and the series page see it. */
export const seriesSummary = z.object({
  seriesKey: z.string(),
  /** The latest instance's title. */
  title: z.string(),
  instances: z.number().int(),
  /** Null for a series nothing of which is recorded yet (PRD 65). */
  lastMetAt: isoDate.nullable(),
  /** When it meets next, where a meeting of it is booked. */
  nextAt: isoDate.nullable(),
  totalSeconds: z.number().int(),
  /** Who is usually there — most frequent first, at most six. */
  regulars: z.array(z.string()),
  /** Open commitments from instances you were in. */
  open: z.object({ youOwe: z.number().int(), owedToYou: z.number().int() }),
  botEnabled: z.boolean(),
  language: z.string(),
  saveVideo: z.boolean(),
});
export type SeriesSummary = z.infer<typeof seriesSummary>;

/** Join this series into another (PRD 60): one series from now on. */
export const seriesJoinBody = z.object({
  /** The series that survives — its settings win, this one's fill its gaps. */
  into: z.string().min(1).max(300),
});
export type SeriesJoinBody = z.infer<typeof seriesJoinBody>;

export const seriesJoined = z.object({
  /** The one series now — where the page goes next. */
  seriesKey: z.string(),
  /** How many meetings moved into it. */
  meetings: z.number().int(),
});
export type SeriesJoined = z.infer<typeof seriesJoined>;
